import type { AvailabilityRules, Board, Entry, Lesson } from '../../../services/timetableService';
import { fitsGrid, sectionLabel } from '../timetableUtils';

/**
 * The board's read model: lookups over one timetable's periods and lessons,
 * and the drop rules that colour cells while a card is dragged. The server
 * applies the same rules to every edit; this lets the board answer instantly.
 */

export type BoardView = 'class' | 'teacher' | 'room' | 'master';

/** What is being dragged: a placed period, or a lesson from the unplaced tray. */
export type Drag =
  | { kind: 'entry'; entry: Entry }
  | { kind: 'lesson'; lesson: Lesson; span: 1 | 2 };

export type DropKind = 'ok' | 'prefer' | 'swap' | 'blocked' | 'off';

export interface DropInfo {
  kind: DropKind;
  reason?: string;
  /** The period a swap would trade places with. */
  target?: Entry;
}

export const sectionKeyOf = (classId: string, sectionInstanceId: string | null) => sectionInstanceId ?? `c${classId}`;
export const entrySectionKey = (e: Pick<Entry, 'classId' | 'sectionInstanceId'>) => sectionKeyOf(e.classId, e.sectionInstanceId);

const covered = (period: number, span: number) => Array.from({ length: span }, (_, i) => period + i);
const overlaps = (a: { day: number; period: number; span: number }, b: { day: number; period: number; span: number }) =>
  a.day === b.day && covered(a.period, a.span).some((p) => covered(b.period, b.span).includes(p));

export interface BoardIndex {
  board: Board;
  /** A period's lesson key; a subject split between teachers has one lesson per teacher. */
  keyOf: (e: Entry) => string;
  lesson: (key: string) => Lesson | undefined;
  lessonOf: (e: Entry) => Lesson | undefined;
  subjectName: (id: string) => string;
  teacherName: (id: string | null) => string;
  roomName: (id: string | null) => string | null;
  sectionLabelOf: (e: Pick<Entry, 'classId' | 'sectionInstanceId'>) => string;
  entriesByDay: Map<number, Entry[]>;
  /** Periods placed per lesson, split into single and double periods. */
  placed: Map<string, { singles: number; doubles: number }>;
  totalRequired: number;
  totalPlaced: number;
}

export function buildIndex(board: Board): BoardIndex {
  const lessons = new Map(board.lessons.map((l) => [l.key, l]));
  const subjects = new Map(board.subjects.map((s) => [s._id, s.name]));
  const teachers = new Map(board.teachers.map((t) => [t._id, t.name]));
  const rooms = new Map(board.rooms.map((r) => [r._id, r.name]));
  const sections = new Map(board.sections.map((s) => [s.key, s.label]));
  const keyOf = (e: Entry) => {
    const group = `${entrySectionKey(e)}:${e.subjectId}`;
    return lessons.has(`${group}:${e.teacherId}`) ? `${group}:${e.teacherId}` : group;
  };
  const entriesByDay = new Map<number, Entry[]>();
  const placed = new Map<string, { singles: number; doubles: number }>();
  let totalPlaced = 0;
  for (const e of board.entries) {
    entriesByDay.set(e.day, [...(entriesByDay.get(e.day) ?? []), e]);
    const key = keyOf(e);
    const cur = placed.get(key) ?? { singles: 0, doubles: 0 };
    if (e.span === 2) cur.doubles += 1; else cur.singles += 1;
    placed.set(key, cur);
    totalPlaced += e.span;
  }
  return {
    board,
    keyOf,
    lesson: (key) => lessons.get(key),
    lessonOf: (e) => lessons.get(keyOf(e)),
    subjectName: (id) => subjects.get(id) ?? 'Subject',
    teacherName: (id) => (id ? teachers.get(id) ?? 'Teacher' : 'No teacher'),
    roomName: (id) => (id ? rooms.get(id) ?? null : null),
    sectionLabelOf: (e) => {
      const key = entrySectionKey(e);
      if (sections.has(key)) return sections.get(key)!;
      const l = board.lessons.find((x) => x.sectionKey === key);
      return l ? sectionLabel(l) : 'Class';
    },
    entriesByDay,
    placed,
    totalRequired: board.lessons.reduce((n, l) => n + l.count, 0),
    totalPlaced,
  };
}

/** What is still to place for a lesson. */
export function remaining(index: BoardIndex, lesson: Lesson) {
  const p = index.placed.get(lesson.key) ?? { singles: 0, doubles: 0 };
  const doubles = Math.max(0, lesson.doubles - p.doubles);
  const placedPeriods = p.singles + 2 * p.doubles;
  const periods = Math.max(0, lesson.count - placedPeriods);
  return { periods, doubles: Math.min(doubles, Math.floor(periods / 2)), singles: Math.max(0, periods - 2 * Math.min(doubles, Math.floor(periods / 2))) };
}

/** Lessons with periods still to place, in board order. */
export function unplacedLessons(index: BoardIndex, filter?: (l: Lesson) => boolean) {
  return index.board.lessons
    .filter((l) => (!filter || filter(l)) && remaining(index, l).periods > 0)
    .map((l) => ({ lesson: l, ...remaining(index, l) }));
}

// ─── Drop rules ──────────────────────────────────────────────────────────

interface Need {
  sectionKey: string;
  teacherId: string | null;
  roomType: string | null;
  label: string;
}

const needOf = (index: BoardIndex, drag: Drag): Need | null => {
  if (drag.kind === 'lesson') {
    const l = drag.lesson;
    return { sectionKey: l.sectionKey, teacherId: l.teacherId, roomType: l.roomType, label: sectionLabel(l) };
  }
  const l = index.lessonOf(drag.entry);
  return {
    sectionKey: entrySectionKey(drag.entry),
    teacherId: drag.entry.teacherId,
    roomType: l?.roomType ?? null,
    label: index.sectionLabelOf(drag.entry),
  };
};

const blocks = (rules: AvailabilityRules | undefined, spot: { day: number; period: number; span: number }, list: 'unavailable' | 'preferNot') =>
  !!rules?.[list].some((s) => s.day === spot.day && covered(spot.period, spot.span).includes(s.period));

/** Whether `need` fits at `spot`, ignoring the periods in `exclude`. */
function check(index: BoardIndex, need: Need, spot: { day: number; period: number; span: number }, exclude: Set<string>): DropInfo {
  const { board } = index;
  const grid = board.timetable!.grid;
  if (!fitsGrid(grid, spot.day, spot.period, spot.span)) {
    return { kind: 'off', reason: spot.span === 2 ? 'A double period needs two periods in a row, with no break between' : undefined };
  }
  const sectionRules = board.availability.section[need.sectionKey];
  const teacherRules = need.teacherId ? board.availability.teacher[need.teacherId] : undefined;
  if (blocks(sectionRules, spot, 'unavailable')) return { kind: 'blocked', reason: `${need.label} is not available then` };
  if (blocks(teacherRules, spot, 'unavailable')) return { kind: 'blocked', reason: `${index.teacherName(need.teacherId)} is not available then` };

  const others = (index.entriesByDay.get(spot.day) ?? []).filter((e) => !exclude.has(e._id) && overlaps(e, spot));
  const sameSection = others.find((e) => entrySectionKey(e) === need.sectionKey);
  if (sameSection) return { kind: 'blocked', reason: `${need.label} has ${index.subjectName(sameSection.subjectId)} then`, target: sameSection };
  const sameTeacher = need.teacherId ? others.find((e) => e.teacherId === need.teacherId) : undefined;
  if (sameTeacher) {
    return { kind: 'blocked', reason: `${index.teacherName(need.teacherId)} teaches ${index.sectionLabelOf(sameTeacher)} then`, target: sameTeacher };
  }
  if (need.roomType) {
    const rooms = board.rooms.filter((r) => r.type === need.roomType && !blocks(board.availability.room[r._id], spot, 'unavailable')).length;
    const using = others.filter((e) => index.lessonOf(e)?.roomType === need.roomType).length;
    if (using >= rooms) return { kind: 'blocked', reason: rooms ? 'Every room of this kind is in use then' : 'No room of this kind is available' };
  }
  if (blocks(sectionRules, spot, 'preferNot') || blocks(teacherRules, spot, 'preferNot')) {
    return { kind: 'prefer', reason: 'Allowed, but marked as better avoided' };
  }
  return { kind: 'ok' };
}

/**
 * What dropping `drag` on (day, period) would do. Dropping a placed period on
 * another one in the same view swaps them when both fit in the other's place.
 */
export function dropInfo(index: BoardIndex, drag: Drag, day: number, period: number, occupant?: Entry): DropInfo {
  const need = needOf(index, drag);
  if (!need) return { kind: 'blocked' };
  const span = drag.kind === 'entry' ? drag.entry.span : drag.span;
  const dragged = drag.kind === 'entry' ? drag.entry : null;
  if (dragged && occupant && occupant._id === dragged._id) return { kind: 'off' };

  if (occupant && dragged) {
    const exclude = new Set([dragged._id, occupant._id]);
    const there = { day: occupant.day, period: occupant.period, span: dragged.span };
    const back = { day: dragged.day, period: dragged.period, span: occupant.span };
    const first = check(index, need, there, exclude);
    if (first.kind === 'blocked' || first.kind === 'off') return { kind: 'blocked', reason: first.reason ?? 'It doesn’t fit there' };
    const otherLesson = index.lessonOf(occupant);
    const otherNeed: Need = {
      sectionKey: entrySectionKey(occupant), teacherId: occupant.teacherId,
      roomType: otherLesson?.roomType ?? null, label: index.sectionLabelOf(occupant),
    };
    const second = check(index, otherNeed, back, exclude);
    if (second.kind === 'blocked' || second.kind === 'off') {
      return { kind: 'blocked', reason: `${index.subjectName(occupant.subjectId)} can’t move here: ${second.reason?.toLowerCase() ?? 'it doesn’t fit'}` };
    }
    if (overlaps(there, back) && (need.sectionKey === otherNeed.sectionKey || (!!need.teacherId && need.teacherId === otherNeed.teacherId))) {
      return { kind: 'blocked', reason: 'They would overlap' };
    }
    return { kind: 'swap', target: occupant };
  }
  if (occupant && !dragged) return { kind: 'blocked', reason: `${index.sectionLabelOf(occupant)} has ${index.subjectName(occupant.subjectId)} then` };
  return check(index, need, { day, period, span }, new Set(dragged ? [dragged._id] : []));
}
